<template>
    <div class="table-container flex-grow-1 pb-0 overflow-x-hidden">
        <div v-if="!tableReady" class="flex align-items-center justify-content-center h-full">
            <ProgressSpinner />
        </div>
        <DataTable
            v-if="tableReady"
            :value="GCMWorks"
            class="p-datatable-sm GCM-works-table"
            scrollable 
            scrollHeight="flex"
            v-model:filters="filters"
            filterDisplay="menu"
            paginator 
            :rows="50"
            columnResizeMode="fit"
            showGridlines
            paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            tableStyle="min-width: 700px;"
            @row-click="openGCMWorksModal">

            <template #header>
                <div class="flex flex-wrap align-items-center justify-content-between gap-2 p-1">
                    <div>
                      <p class="title"><span class="text-xl text-900 font-bold">GCM Works</span></p>
                      <p class="subtitle"><span><small>Last Updated: {{ GCMWorksLastUpdated }}</small></span></p>
                    </div>
                    
                    <Button rounded raised severity="secondary" :loading="loading" @click="sync">
                        <template #icon>
                            <span class="material-symbols-outlined">download</span>
                        </template>
                    </Button>
                </div>
            </template>
            <Column field="tableGlobalFilterValues" header="Activity" frozen style="width: 200px" filterField="tableGlobalFilterValues" :showFilterMatchModes="false">
              <template #filter="{ filterModel }">
                <SerialNumberFilter
                  v-model="filterModel.value"
                  :activityOptions="activityOptions"
                />
              </template>
              <template #body="slotProps">
                {{ slotProps.data.displayName() }}
              </template>
            </Column>
            <Column field="completion_time" header="Completion Time" :showFilterMatchModes="false">
                <template #filter="{ filterModel }">
                    <div>
                        <Calendar v-model="filterModel.value" inline />
                    </div>
                </template>
                <template #body="slotProps">
                    {{ slotProps.data.completionTimeDisplay() }}
                </template>
            </Column>
            <Column field="supervisor" header="Supervisor" filterField="supervisor" :showFilterMatchModes="false">
                <template #filter="{ filterModel }">
                    <div v-for="supervisor in supervisorOptions" :key="supervisor.value" class="flex align-items-center" style="margin-top: 10px">
                        <Checkbox v-model="filterModel.value" :inputId="supervisor.value" name="activity" :value="supervisor.value" style="margin-right: 5px;" />
                        <label :for="supervisor.value">{{ supervisor.display }}</label>
                    </div>
                </template>
                <template #body="slotProps">
                    {{ slotProps.data.supervisor.displayName() }}
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script>
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import ProgressSpinner from 'primevue/progressspinner';
import Calendar from 'primevue/calendar';
import Checkbox from 'primevue/checkbox';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';

import { useTarpRepairsStore } from '@/stores/TarpRepairs';
import { useTarpJoinsStore } from '@/stores/TarpJoins';
import { useStockpilingStore } from '@/stores/Stockpiling';
import { useNewTarpsInputsStore } from '@/stores/NewTarpsInputs';

import GCMWorksDetails from './GCMWorksDetails.vue';
import SerialNumberFilter from './SerialNumberFilter.vue';

import { FilterMatchMode, FilterService } from 'primevue/api';


FilterService.register('filterGCMByGlobal', (globalValues, filterValue) => {
  const { serialNo, activityTypes } = filterValue;

  // Check if any of the GCM work activities are in the filter array
  if (!serialNo && activityTypes.length === 0) return true;
  if (!globalValues || globalValues.length === 0) return false;

  // If serial number is not empty, check if search value is substring of serial number
  if (serialNo) {
    const serialNoMatch = globalValues.some(val => val.includes(serialNo));
    if (!serialNoMatch) return false;
  }

  // If activity types is not empty, check if any of the activities match
  if (activityTypes.length > 0) {
    return globalValues.some(val => activityTypes.includes(val));
  }

  return true;
  
});

FilterService.register('filterByCompletionTime', (completionTime, date) => {
    // Return true if any of the GCM work activities are in the filter array
    if (!date) return true;
    // Check if dates match (compare date only, not time)
    const date1 = new Date(completionTime).toDateString();
    const date2 = new Date(date).toDateString();
    return date1 === date2;
});

FilterService.register('filterBySupervisor', (supervisor, filterArray) => {
    // Return true if any of the GCM work activities are in the filter array
    if (!filterArray || filterArray.length === 0) return true;
    return filterArray.includes(supervisor.email);
});


export default {
    name: "GCMWorksTable",
    components: {
        Button,
        DataTable,
        ProgressSpinner,
        Calendar,
        Checkbox,
        Column,
        InputText,
        MultiSelect,
        GCMWorksDetails,
        SerialNumberFilter,
    },
    data() { return {
        tableReady: false,
        loading: false,
        filters: {
          completion_time: { value: null, matchMode: 'filterByCompletionTime' },
          supervisor: { value: null, matchMode: 'filterBySupervisor' },
          tableGlobalFilterValues: { 
            value: null,
            matchMode: 'filterGCMByGlobal' 
          },
        },
    }},
    computed: {
        GCMWorks() {
            const tarpRepairs = this.tarpRepairsStore.getTarpRepairs;
            const tarpJoins = this.tarpJoinsStore.getTarpJoins;
            const stockpiling = this.stockpilingStore.getStockpiling;
            const newTarpsInputs = this.newTarpsInputsStore.getNewTarpsInputs;

            const GCMWorks = [
                ...tarpRepairs,
                ...tarpJoins,
                ...stockpiling,
                ...newTarpsInputs,
            ].sort((a, b) => {
                return b.completion_time - a.completion_time;
            })
            return GCMWorks
        },
        GCMWorksLastUpdated() {
            // Format store last updated date for display
            const date = new Date(this.tarpRepairsStore.lastUpdated);
            return date.toLocaleString();
        },
        supervisorOptions() {
            // Get unique supervisors from OBHM works
            const supervisors = this.GCMWorks.map(gcm => gcm.supervisor);
            const supervisorEmails = [...new Set(this.GCMWorks.map(gcm => gcm.supervisor.email))];
            const supervisorOptions = supervisorEmails.map(email => {
                const supervisor = supervisors.find(supervisor => supervisor.email === email);
                return {
                    display: `${supervisor.displayName()}`,
                    value: `${email}`,
                }
            });
            // Sort supervisor options by display name
            supervisorOptions.sort((a, b) => {
                if (a.display < b.display) return -1;
                if (a.display > b.display) return 1;
                return 0;
            });
            return supervisorOptions;
        },
        activityOptions() {
          return [
            {
              value: 'tarp_repair',
              display: 'Tarp Repairs',
            },
            {
              value: 'tarp_join',
              display: 'Tarp Joins',
            },
            {
              value: 'stockpiling',
              display: 'Stockpiling',
            },
            {
              value: 'new_tarps_input',
              display: 'New Tarps Input',
            },
          ]
        }
    },
    async created() {
        this.tarpRepairsStore = useTarpRepairsStore();
        this.tarpJoinsStore = useTarpJoinsStore();
        this.stockpilingStore = useStockpilingStore();
        this.newTarpsInputsStore = useNewTarpsInputsStore();

        await new Promise(resolve => setTimeout(resolve, 2000));
        this.tableReady = true;
    },
    methods: {
        async sync() {
            this.loading = true;
            try {
                const promises = [
                    this.tarpRepairsStore.loadTarpRepairs(true),
                    this.tarpJoinsStore.loadTarpJoins(true),
                    this.stockpilingStore.loadStockpiling(true),
                    this.newTarpsInputsStore.loadNewTarpsInputs(true),
                ];

                await Promise.all(promises);

                this.$root.showNotification(
                    'success',
                    'GCM works synced successfully.',
                );
            } catch (error) {
                console.error(error);
                this.$root.showNotification(
                    'error',
                    'Failed to sync GCM works.',
                );
            } finally {
                this.loading = false;
            }
        },
        openGCMWorksModal(event) {
            const gcmWorks = event.data;
            const dialogRef = this.$dialog.open(GCMWorksDetails, {
                props: {
                    header: gcmWorks.displayName(),
                    style: {
                        width: '100vw',
                        height: '100vh',
                        maxHeight: '100vh',
                        borderRadius: '0',
                    },
                    modal: true,
                    dismissableMask: true,
                    contentClass: 'flex-grow-1',
                },
                data: {
                    gcmWorks: gcmWorks,
                },
            });
        }
    }
}

</script>

<style>
.table-container {
    overflow: scroll;
}
.GCM-works-table table {
    table-layout: fixed;
}
.GCM-works-table .p-datatable-table .p-datatable-thead > tr > th {
    background: #f1f3f4;
}
.GCM-works-table .p-datatable-tbody {
    font-size: 14px;
}
</style>